// Feed placement chrome — Instagram Feed and Facebook Feed.
//
// Unlike Reels/Stories these placements draw nothing on the creative:
// the platform furniture sits above and below it in a card. So these
// components WRAP the media rather than overlay it, and the creative
// stays fully visible — which is the point, since a feed buy is judged
// on how the creative reads against the surrounding card.
//
// Geometry is in `u` = 1% of media width, matching overlays.tsx.

import type { CSSProperties, ReactNode } from 'react';
import { Icon, Avatar } from './icons';
import { displayDomain } from './overlays';
import type { ChromeDensity, ChromeIdentity } from './types';

const FONT = "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const LOREM = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor';
const LOREM_SHORT = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';

type CardProps = {
  identity:        ChromeIdentity;
  u:               number;
  density:         ChromeDensity;
  showSponsored?:  boolean;
  showEngagement?: boolean;
  children:        ReactNode;
};

const cardBase: CSSProperties = {
  background: '#fff', color: '#111', overflow: 'hidden',
  fontFamily: FONT, textAlign: 'left', width: '100%'
};

// ── Instagram Feed ───────────────────────────────────────────────────

export function IgFeedCard({
  identity, u, density, showSponsored = true, showEngagement = true, children
}: CardProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop now';
  // Grid tiles are height-starved: keep the card's structure but tighten
  // the vertical rhythm so the creative keeps most of the tile.
  const p = compact ? 0.6 : 1;

  return (
    <div style={cardBase}>
      <div style={{ display: 'flex', alignItems: 'center', gap: u * 2.4, padding: `${u * 2.6 * p}px ${u * 3.2}px` }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 8} />
        <div style={{ lineHeight: 1.25, minWidth: 0 }}>
          <b style={{ display: 'block', fontSize: u * 3.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {identity.handle}
          </b>
          {showSponsored && <span style={{ fontSize: u * 3, color: '#737373' }}>Sponsored</span>}
        </div>
        <span style={{ marginLeft: 'auto', color: '#262626', flex: 'none' }}><Icon name="dots" size={u * 5} /></span>
      </div>

      {children}

      {/* CTA band directly under the media — a full-bleed row, not an
          inset pill, and dark-on-near-white rather than link blue. That
          near-invisible band is what separates an ad from an organic
          post, so drawing it as a bright button reads wrong. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${u * 2.8 * p}px ${u * 3.2}px`,
        background: '#fafafa', color: '#000', fontWeight: 600,
        fontSize: u * 3.4, borderBottom: '1px solid #dbdbdb'
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cta}</span>
        <Icon name="chevR" size={u * 4} style={{ strokeWidth: 2.4 }} />
      </div>

      {showEngagement && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: u * 4,
          padding: `${u * 2.8 * p}px ${u * 3.2}px ${(compact ? u * 2.8 : u * 1.6) * p}px`, color: '#262626'
        }}>
          <Icon name="heart" size={u * 6} />
          <Icon name="comment" size={u * 6} />
          <Icon name="send" size={u * 6} />
          <span style={{ marginLeft: 'auto' }}><Icon name="bookmark" size={u * 6} /></span>
        </div>
      )}

      {showEngagement && !compact && (
        <>
          <div style={{ padding: `0 ${u * 3.2}px`, fontWeight: 700, fontSize: u * 3.4 }}>1,248 likes</div>
          <div style={{ padding: `${u * 1.2}px ${u * 3.2}px ${u * 3}px`, fontSize: u * 3.3, lineHeight: 1.4, color: '#262626' }}>
            <b>{identity.handle}</b> {LOREM.slice(0, 62)}&hellip; <span style={{ color: '#737373' }}>more</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Facebook Feed ────────────────────────────────────────────────────

export function FbFeedCard({
  identity, u, density, showSponsored = true, showEngagement = true, children
}: CardProps) {
  const compact = density === 'compact';
  const cta = identity.ctaText || 'Shop Now';
  const p = compact ? 0.6 : 1;
  const domain = displayDomain(identity.linkDomain);

  return (
    <div style={cardBase}>
      <div style={{ display: 'flex', alignItems: 'center', gap: u * 2.4, padding: `${u * 2.6 * p}px ${u * 3.2}px` }}>
        <Avatar logoUrl={identity.logoUrl} name={identity.name} size={u * 10} />
        <div style={{ lineHeight: 1.25, minWidth: 0 }}>
          <b style={{ display: 'block', fontSize: u * 3.8, color: '#050505', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {identity.name}
          </b>
          {showSponsored && (
            <span style={{ fontSize: u * 3.2, color: '#65676b', display: 'flex', alignItems: 'center', gap: u }}>
              Sponsored &middot; <Icon name="globe" size={u * 3} />
            </span>
          )}
        </div>
        <span style={{ marginLeft: 'auto', color: '#65676b', flex: 'none' }}><Icon name="dots" size={u * 5} /></span>
      </div>

      {showEngagement && !compact && (
        <div style={{ padding: `0 ${u * 3.2}px ${u * 2.6}px`, fontSize: u * 3.5, lineHeight: 1.4, color: '#050505' }}>
          {LOREM_SHORT}
        </div>
      )}

      {children}

      {/* Grey link-preview row. Meta began omitting the display URL on
          some single-media feed ads in 2026; the headline + CTA row
          itself is unchanged, so we keep the domain line — it's the
          more conservative preview. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: u * 3,
        background: '#f0f2f5', padding: `${u * 2.6 * p}px ${u * 3.2}px`
      }}>
        <div style={{ minWidth: 0 }}>
          <i style={{ display: 'block', fontStyle: 'normal', fontSize: u * 2.9, color: '#65676b', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {domain}
          </i>
          <b style={{ display: 'block', fontSize: u * 3.6, color: '#050505', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Lorem ipsum dolor sit amet
          </b>
          {!compact && (
            <span style={{ display: 'block', fontSize: u * 3.1, color: '#65676b' }}>Consectetur adipiscing elit</span>
          )}
        </div>
        <span style={{
          flex: 'none', background: '#e4e6eb', color: '#050505', borderRadius: u * 1.5,
          fontWeight: 700, fontSize: u * 3.3, padding: `${u * 2.2 * p}px ${u * 3.6}px`, fontFamily: FONT,
          whiteSpace: 'nowrap'
        }}>
          {cta}
        </span>
      </div>

      {showEngagement && !compact && (
        <div style={{ display: 'flex', borderTop: '1px solid #e4e6eb', marginTop: u * 0.5 }}>
          {([['thumb', 'Like'], ['comment', 'Comment'], ['shareFb', 'Share']] as Array<[string, string]>).map(([name, label]) => (
            <div key={label} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: u * 1.6,
              color: '#65676b', fontWeight: 700, fontSize: u * 3.3, padding: `${u * 3}px 0`
            }}>
              <Icon name={name} size={u * 4.4} /> {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
